import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import type { PlanStatus, Tenant, TenantView } from "@vereinfach/types";
import { Shell } from "../components/Shell";
import { useAuth } from "../lib/auth";
import { useImpersonation } from "../lib/impersonation";
import { SettingsProvider } from "../lib/settings";
import { CurrentTenantProvider } from "../lib/currentTenant";
import { api } from "../lib/api";

export function DashLayout() {
  const { me, onLoggedOut } = useAuth();
  const { activeTenantId } = useImpersonation();
  const navigate = useNavigate();
  const [impersonatedTenant, setImpersonatedTenant] = useState<TenantView | null>(null);
  const [loadingImpersonated, setLoadingImpersonated] = useState(false);

  const ownTenant = me?.tenants[0] ?? null;
  const shouldImpersonate = !!(me?.is_admin && activeTenantId);

  useEffect(() => {
    if (!shouldImpersonate || !activeTenantId) {
      setImpersonatedTenant(null);
      return;
    }
    setLoadingImpersonated(true);
    // The admin endpoint returns a plain Tenant; the entitlement comes from the
    // tenant-scoped /plan status so impersonated views still see the live cap.
    Promise.all([
      api<Tenant>(`/_admin/tenants/${activeTenantId}`),
      api<{ plan: PlanStatus }>(`/tenants/${activeTenantId}/plan`).catch(() => null),
    ])
      .then(([tenant, planRes]) => {
        const entitlement = planRes
          ? { plan_id: planRes.plan.plan_id, cap: planRes.plan.cap }
          : { plan_id: "frei" as const, cap: null };
        setImpersonatedTenant({ ...tenant, entitlement });
      })
      .catch(() => setImpersonatedTenant(null))
      .finally(() => setLoadingImpersonated(false));
  }, [shouldImpersonate, activeTenantId]);

  const tenant = shouldImpersonate ? (impersonatedTenant ?? ownTenant) : ownTenant;

  // Admin with no own tenant AND no impersonation set: bounce to /admin so
  // they can pick one. Crucially we bail out as soon as `activeTenantId` is
  // set — before the impersonated fetch completes — so clicking "Verwalten"
  // on the admin dashboard doesn't race this effect and redirect the admin
  // back to where they just came from.
  useEffect(() => {
    if (!me?.is_admin) return;
    if (activeTenantId) return;
    if (!ownTenant) navigate({ to: "/admin", replace: true });
  }, [me?.is_admin, activeTenantId, ownTenant, navigate]);

  if (shouldImpersonate && loadingImpersonated) {
    return <div className="min-h-full flex items-center justify-center text-slate-400">Lädt…</div>;
  }

  if (!tenant) {
    return <div className="min-h-full flex items-center justify-center text-slate-400">Lädt…</div>;
  }

  return (
    <CurrentTenantProvider tenant={tenant}>
      <SettingsProvider tenantId={tenant.id}>
        <Shell
          tenant={tenant}
          onLoggedOut={onLoggedOut}
          impersonating={shouldImpersonate && !!impersonatedTenant}
        >
          <Outlet />
        </Shell>
      </SettingsProvider>
    </CurrentTenantProvider>
  );
}
